import { t } from "i18next";
import { ng } from "@common/ngds";
import { getSectionGeometry, normalizeSectionLength } from "./sectionGeometry";
import type { SectionGeometry } from "./sectionGeometry";

/*
Sanity check of a reinforcement entered bar by bar ("Exact bar coordinates").

In that mode the user types each bar's position and diameter, and nothing stops a bar being placed
outside the concrete or two bars sitting on top of each other — "Add rebar" drops every new bar at
the centroid, so the second click already overlaps the first. The engine takes the bars as given and
produces results for a column that cannot exist.

The check runs when an analysis or a code check is started (runCalcThunk), and nowhere else. A bar
layout is built one click at a time and is wrong in the middle — a bar that has been added but not yet
moved into place — so checking as the user edits would nag about a layout they know is unfinished.
Starting a calculation is the point where they have said it is finished. The automatic
analyse-on-save in syncMiddleware skips a failing layout silently, for the same reason: it was not
asked for.

Only what makes the input meaningless is checked here — things no calculation result can be trusted
over. Detailing rules (cover, clear spacing, a minimum number of bars) are the code check's business.

The failing bars are also marked in the bar table and the cross-section view (useExactBarMarks). The
marks follow the same rule: set when a calculation is refused, then only ever removed — a mark stays
while its bar still fails and goes the moment it is fixed, but an edit that breaks another bar marks
nothing until the next press.
*/

/** Slack for bars placed exactly against a face or exactly touching: display rounding, not a mistake. */
const TOLERANCE_M = 1.0e-6;

/** More lines than this in one toast is a wall of text; the first few say what kind of mess it is. */
const MAX_LISTED_ISSUES = 8;

// `guids` are the bars the issue is about, so each can be marked where it is drawn. Bars are
// identified by guid rather than number: deleting a row renumbers every row after it.
type ExactBarIssue =
  | { kind: "no-bars"; guids: [] }
  | { kind: "no-diameter"; bar: number; guids: string[] }
  | { kind: "no-material"; bar: number; guids: string[] }
  | { kind: "outside-section"; bar: number; guids: string[] }
  | { kind: "protrudes-section"; bar: number; guids: string[] }
  | { kind: "overlap"; bar: number; other: number; guids: string[] };

type CheckedBar = { number: number; guid: string; y: number; z: number; radius: number };

/**
 * Whether a circle of this radius around the bar's centre lies inside the concrete outline. Asked with
 * radius 0 it answers for the centre alone.
 */
const fitsInsideSection = (bar: CheckedBar, radius: number, section: SectionGeometry): boolean => {
  if (section.circular) {
    return Math.hypot(bar.y, bar.z) + radius <= section.radius + TOLERANCE_M;
  }

  return (
    bar.y - radius >= section.minY - TOLERANCE_M &&
    bar.y + radius <= section.maxY + TOLERANCE_M &&
    bar.z - radius >= section.minZ - TOLERANCE_M &&
    bar.z + radius <= section.maxZ + TOLERANCE_M
  );
};

/**
 * What is wrong with the bars of an exact-coordinate reinforcement, bars numbered from 1 as in the
 * reinforcement table. Empty when the column is not in that mode, or nothing is wrong.
 */
const findExactBarIssues = (input: ng.input.Data_json | null | undefined): ExactBarIssue[] => {
  const elementJson = input?.structures?.[0]?.elements?.[0];
  const beam = elementJson ? ng.element.Data.fromObject(elementJson).getBeam() : null;
  const rebars = beam?.getConcrete()?.rebars;
  if (!beam || !rebars?.exactBarCoordinates) {
    return [];
  }

  // The engine prefers the standalone bars and falls back to the grid layers only when there are
  // none, so an emptied table would silently calculate the grid layout the user switched away from.
  if (rebars.standaloneBars.length === 0) {
    return [{ kind: "no-bars", guids: [] }];
  }

  const issues: ExactBarIssue[] = [];
  const materialGuids = new Set((input?.mtrlDb ?? []).map((material) => material?.id?.guid));
  const secGuid = beam.segments?.[0]?.layer?.start?.secGuid;
  const secJson = input?.secDb?.find((section) => section?.id?.guid === secGuid);
  const section = secJson ? getSectionGeometry(ng.sections.Section.fromObject(secJson)) : null;

  const bars: CheckedBar[] = rebars.standaloneBars.map((bar, index) => ({
    number: index + 1,
    guid: bar.id?.guid ?? "",
    y: Number(bar.startPoint?.y ?? 0),
    z: Number(bar.startPoint?.z ?? 0),
    radius: normalizeSectionLength(Number(bar.diameter ?? 0)) / 2,
  }));

  rebars.standaloneBars.forEach((bar, index) => {
    const checked = bars[index];
    const guids = checked.guid ? [checked.guid] : [];
    if (!(checked.radius > 0)) {
      issues.push({ kind: "no-diameter", bar: checked.number, guids });
    }
    // The engine resolves the material in mtrlDb and fails the run without naming the bar.
    if (!bar.mtrlGuid || !materialGuids.has(bar.mtrlGuid)) {
      issues.push({ kind: "no-material", bar: checked.number, guids });
    }
    // The whole bar must be covered by concrete, not just its centre — but by how much (the cover)
    // is a code check matter and deliberately not asked here. A bar whose centre is inside but whose
    // perimeter pokes out is told apart from one placed outside altogether: the first wants a nudge
    // inwards, the second is most likely a typo in a coordinate.
    // An outline this app cannot read (none of rectangle, square, circle) is left unchecked rather
    // than guessed at.
    if (section && !fitsInsideSection(checked, checked.radius, section)) {
      const centreInside = fitsInsideSection(checked, 0, section);
      issues.push({ kind: centreInside ? "protrudes-section" : "outside-section", bar: checked.number, guids });
    }
  });

  // Touching is allowed (bundled bars); only a real intersection is reported.
  for (let i = 0; i < bars.length; i++) {
    for (let j = i + 1; j < bars.length; j++) {
      const a = bars[i];
      const b = bars[j];
      if (Math.hypot(a.y - b.y, a.z - b.z) + TOLERANCE_M < a.radius + b.radius) {
        const guids = [a.guid, b.guid].filter((guid) => guid !== "");
        issues.push({ kind: "overlap", bar: a.number, other: b.number, guids });
      }
    }
  }

  return issues;
};

const describeIssue = (issue: ExactBarIssue): string => {
  switch (issue.kind) {
    case "no-bars":
      return t("exact_bars_none_.label", "No bars have been entered.");
    case "no-diameter":
      return t("exact_bars_no_diameter_.label", "Bar {{bar}} has no diameter.", { bar: issue.bar });
    case "no-material":
      return t("exact_bars_no_material_.label", "Bar {{bar}} has no reinforcement material.", { bar: issue.bar });
    case "outside-section":
      return t("exact_bars_outside_section_.label", "Bar {{bar}} lies outside the concrete section.", {
        bar: issue.bar,
      });
    case "protrudes-section":
      return t("exact_bars_protrudes_section_.label", "Bar {{bar}} protrudes outside the concrete section.", {
        bar: issue.bar,
      });
    case "overlap":
      return t("exact_bars_overlap_.label", "Bars {{bar}} and {{other}} overlap.", {
        bar: issue.bar,
        other: issue.other,
      });
  }
};

/**
 * The localised reason a calculation must not start on this input, one issue per line, or null when
 * the reinforcement is fine (or not entered bar by bar at all).
 */
export const exactBarCheckMessage = (input: ng.input.Data_json | null | undefined): string | null => {
  const issues = findExactBarIssues(input);
  if (issues.length === 0) {
    return null;
  }

  const lines = issues.slice(0, MAX_LISTED_ISSUES).map((issue) => `• ${describeIssue(issue)}`);
  if (issues.length > MAX_LISTED_ISSUES) {
    lines.push(t("exact_bars_more_.label", "…and {{count}} more.", { count: issues.length - MAX_LISTED_ISSUES }));
  }

  return [
    t("exact_bars_header_.label", "Fix the reinforcement bar coordinates before running the calculation:"),
    ...lines,
  ].join("\n");
};

/**
 * The bars that fail the check, by guid, each with the localised reasons it fails — the same sentences
 * the toast lists, with the bar numbers as the table shows them now. Empty when nothing is wrong.
 */
export const failingExactBars = (input: ng.input.Data_json | null | undefined): Map<string, string[]> => {
  const failing = new Map<string, string[]>();
  for (const issue of findExactBarIssues(input)) {
    for (const guid of issue.guids) {
      failing.set(guid, [...(failing.get(guid) ?? []), describeIssue(issue)]);
    }
  }
  return failing;
};
